import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConnectors, useMetaOtp } from "@/features/connectors";
import { useTranslation } from "@/features/utils/i18n";
import { CircleNotch, Copy } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

/** The server stops keeping anything 15 minutes after the last look. */
const LISTEN_MS = 15 * 60 * 1000;

/**
 * The code Meta sends to a number to prove it is ours, for a number with no
 * handset — a HeyUp line. Meta texts it or reads it out on a call; both land on
 * our line, and this panel shows what we caught. It listens only while this
 * panel is looking: the first look checks the number is the company's own.
 */
export const OtpHelper = ({ clientId }: { clientId: string }) => {
  const { t } = useTranslation("connectors");
  const { data: connectors = [] } = useConnectors(clientId);

  // Numbers that can be a WhatsApp sender: the company's own lines.
  const numbers = useMemo(
    () =>
      [
        ...new Set(
          connectors
            .filter((c) => c.type === "heyup" || c.type === "twilio")
            .flatMap((c) => c.configuration?.phone_numbers ?? []),
        ),
      ].sort(),
    [connectors],
  );

  const [number, setNumber] = useState("");
  const [listening, setListening] = useState(false);
  // `received_at` of a code the user rejected: only a newer one is wanted.
  const [after, setAfter] = useState(0);
  const [expired, setExpired] = useState(false);

  const { data, error } = useMetaOtp(clientId, number, listening && !expired, after);
  const otp = data?.otp ?? null;

  useEffect(() => {
    if (!listening) return;
    setExpired(false);
    const timer = setTimeout(() => setExpired(true), LISTEN_MS);
    return () => clearTimeout(timer);
  }, [listening, number, after]);

  useEffect(() => {
    if (error) {
      toast.error(
        error instanceof Error ? error.message : t("meta.otp.armError"),
      );
      setListening(false);
    }
  }, [error, t]);

  if (numbers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t("meta.otp.noNumber")}</p>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <p className="text-sm text-muted-foreground">{t("meta.otp.intro")}</p>

      <div className="space-y-2">
        <Label htmlFor="meta-otp-number">{t("meta.otp.number")}</Label>
        <Select
          value={number}
          onValueChange={(value) => {
            setNumber(value);
            setListening(false);
            setAfter(0);
          }}
        >
          <SelectTrigger id="meta-otp-number">
            <SelectValue placeholder={t("meta.otp.numberPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {numbers.map((n) => (
              <SelectItem key={n} value={n}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!listening && (
        <Button
          type="button"
          variant="outline"
          disabled={!number}
          onClick={() => setListening(true)}
        >
          {t("meta.otp.listen")}
        </Button>
      )}

      {listening && !otp && !expired && (
        <p className="flex items-center gap-2 text-sm">
          <CircleNotch className="h-4 w-4 animate-spin" />
          {t("meta.otp.waiting")}
        </p>
      )}

      {listening && !otp && expired && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("meta.otp.expired")}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setExpired(false);
              setAfter((value) => value);
            }}
          >
            {t("meta.otp.listenAgain")}
          </Button>
        </div>
      )}

      {otp && (
        <div className="space-y-2">
          <div className="flex items-center justify-between rounded-md bg-muted px-4 py-3">
            <div>
              <p className="text-xs text-muted-foreground">
                {t("meta.otp.received")}
              </p>
              <p className="font-mono text-2xl tracking-widest">
                {otp.code.replace(/(\d{3})(\d{3})/, "$1 $2")}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("meta.otp.copy")}
              onClick={() => {
                void navigator.clipboard?.writeText(otp.code);
                toast.success(t("meta.otp.copied"));
              }}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <button
            type="button"
            className="text-xs underline underline-offset-4"
            onClick={() => setAfter(otp.received_at)}
          >
            {t("meta.otp.wrongCode")}
          </button>
        </div>
      )}
    </div>
  );
};
